import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import colors from '../../utils/colors';
import Cell from '../ui/Cell';
import LargeButton from '../ui/LargeButton';
import RemixSubmenu from '../ui/RemixSubmenu';
import PlaylistCard from '../ui/PlaylistCard';
import DropdownMenu from '../ui/DropdownMenu';
import { SmallSpinner } from '../../utils/loaders';
import {
  UploadIcon,
  RecordIcon,
  LibraryIcon
} from '../../utils/icons';

// Simple mic icon
const MicIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7.96496 9.33325C7.41163 9.33325 6.93829 9.13992 6.55163 8.74659C6.16496 8.35992 5.96496 7.88659 5.96496 7.33325V3.33325C5.96496 2.77992 6.15829 2.30659 6.55163 1.91992C6.93829 1.53325 7.41163 1.33325 7.96496 1.33325C8.5183 1.33325 8.99163 1.52659 9.37829 1.91992C9.76496 2.31325 9.96496 2.77992 9.96496 3.33325V7.33325C9.96496 7.88659 9.77163 8.35992 9.37829 8.74659C8.98496 9.13325 8.5183 9.33325 7.96496 9.33325ZM7.96496 13.9999C7.5983 13.9999 7.2983 13.6999 7.2983 13.3333V12.5333C7.2983 12.1999 7.05163 11.9199 6.72496 11.8399C5.82496 11.6066 5.05829 11.1266 4.43163 10.3999C3.82496 9.69325 3.45829 8.89325 3.33829 7.99992C3.29163 7.63325 3.59163 7.33325 3.96496 7.33325C4.33163 7.33325 4.62496 7.63325 4.69163 7.99325C4.81163 8.63325 5.1183 9.19992 5.60496 9.68659C6.2583 10.3333 7.0383 10.6599 7.96496 10.6599C8.89163 10.6599 9.67163 10.3333 10.325 9.68659C10.8116 9.19992 11.1183 8.63325 11.2383 7.99325C11.305 7.63325 11.5983 7.33325 11.965 7.33325C12.3316 7.33325 12.6383 7.63325 12.5916 7.99992C12.4716 8.89325 12.1116 9.69325 11.4983 10.3999C10.8716 11.1266 10.105 11.6066 9.20496 11.8399C8.8783 11.9199 8.63163 12.1999 8.63163 12.5333V13.3333C8.63163 13.6999 8.33163 13.9999 7.96496 13.9999Z"
      fill="currentColor"
    />
  </svg>
);

// Sound Wave Animation Component
const SoundWave = () => (
  <div className="flex items-center justify-center gap-1">
    <div className="w-1 bg-white rounded-full animate-pulse" style={{ 
      height: '16px',
      animation: 'soundWave1 1.2s ease-in-out infinite'
    }} />
    <div className="w-1 bg-white rounded-full animate-pulse" style={{ 
      height: '12px',
      animation: 'soundWave2 1.2s ease-in-out infinite'
    }} />
    <div className="w-1 bg-white rounded-full animate-pulse" style={{ 
      height: '20px',
      animation: 'soundWave3 1.2s ease-in-out infinite'
    }} />
    <div className="w-1 bg-white rounded-full animate-pulse" style={{ 
      height: '14px',
      animation: 'soundWave4 1.2s ease-in-out infinite'
    }} />
  </div>
);

// New Song Card Component
const SongCard = ({ song, isPlaying, onPlay, onPause, onRemix, size = 200 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showRemixSubmenu, setShowRemixSubmenu] = useState(false);
  const remixButtonRef = useRef(null);
  const submenuRef = useRef(null);

  const handleRemixClick = (e) => {
    e.stopPropagation();
    setShowRemixSubmenu(!showRemixSubmenu);
  };

  const handleRemixAction = (action) => {
    console.log(`Remix action: ${action} for song:`, song);
    setShowRemixSubmenu(false);
    setIsHovered(false);
    onRemix?.(action, song);
  };

  // Close submenu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showRemixSubmenu) {
        // Check if the click is outside both the submenu and the remix button
        const clickedOutsideSubmenu = submenuRef.current && !submenuRef.current.contains(event.target);
        const clickedOutsideButton = remixButtonRef.current && !remixButtonRef.current.contains(event.target);
        
        if (clickedOutsideSubmenu && clickedOutsideButton) {
          setShowRemixSubmenu(false);
        }
      }
    };

    if (showRemixSubmenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRemixSubmenu]);

  return (
    <div
      className="cursor-pointer transition-transform duration-200 hover:scale-[1.02]"
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        flexGrow: 0,
        flexShrink: 0,
        gap: 12,
        position: "relative",
        zIndex: showRemixSubmenu ? 1000 : 1
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => isPlaying ? onPause() : onPlay(song)}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexGrow: 0,
          flexShrink: 0,
          height: size,
          width: size,
          overflow: "hidden",
          padding: 8,
          borderRadius: 16,
          background: "linear-gradient(-1.68deg, rgba(0,0,0,0) 17.32%, rgba(20,20,22,0.3) 100.12%)",
          borderWidth: "0.5px",
          borderColor: "rgba(255,255,255,0.1)",
          borderStyle: "solid",
          backgroundImage: `url(${song.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundClip: "padding-box",
          position: "relative"
        }}
      >
        
        {/* Top section with play count */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            alignSelf: "stretch",
            flexGrow: 0,
            flexShrink: 0,
            position: "relative",
            borderRadius: 40,
            zIndex: 3,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              flexGrow: 0,
              flexShrink: 0,
              gap: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexGrow: 0,
                flexShrink: 0,
                height: 20,
                position: "relative",
                gap: 2,
                paddingRight: 4,
                borderRadius: 100,
              }}
            >
              <svg
                width={16}
                height={16}
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ flexGrow: 0, flexShrink: 0, width: 16, height: 16, position: "relative" }}
                preserveAspectRatio="xMidYMid meet"
              >
                <path
                  d="M4 12.4695V3.5302C4 3.28549 4.09218 3.08036 4.27654 2.91482C4.46091 2.74927 4.67599 2.6665 4.92181 2.6665C4.99863 2.6665 5.07929 2.6773 5.16379 2.69889C5.24829 2.72049 5.32894 2.75287 5.40576 2.79606L12.9185 7.26569C13.0568 7.35206 13.1605 7.46003 13.2296 7.58958C13.2988 7.71914 13.3333 7.85589 13.3333 7.99984C13.3333 8.14379 13.2988 8.28054 13.2296 8.41009C13.1605 8.53965 13.0568 8.64761 12.9185 8.73398L5.40576 13.2036C5.32894 13.2468 5.24829 13.2792 5.16379 13.3008C5.07929 13.3224 4.99863 13.3332 4.92181 13.3332C4.67599 13.3332 4.46091 13.2504 4.27654 13.0849C4.09218 12.9193 4 12.7142 4 12.4695Z"
                  fill="white"
                  fillOpacity="0.8"
                />
              </svg>
              <p
                style={{
                  flexGrow: 0,
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 500,
                  textAlign: "left",
                  color: "rgba(255,255,255,0.8)",
                }}
              >
                {song.playCount || '13.3k'}
              </p>
            </div>
          </div>
          
          {/* Top right actions on hover */}
          {isHovered && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                flexGrow: 0,
                flexShrink: 0,
                gap: 8,
              }}
            >
              {/* Remix Icon */}
              <div
                ref={remixButtonRef}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: 24,
                  height: 24,
                  cursor: "pointer",
                  position: "relative"
                }}
                onClick={handleRemixClick}
              >
                <svg
                  width={20}
                  height={20}
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexGrow: 0, flexShrink: 0, width: 20, height: 20, position: "relative" }}
                  preserveAspectRatio="xMidYMid meet"
                >
                  <path
                    d="M2.66683 11.8107C2.78144 11.395 3.19138 11.14 3.60758 11.2101L3.6914 11.2288L6.10189 11.8929C6.54542 12.0152 6.80593 12.4739 6.68375 12.9175C6.56148 13.3611 6.10282 13.6215 5.65918 13.4993L5.25146 13.387C6.30923 14.8671 8.03972 15.8331 9.99756 15.8333C12.1262 15.8333 13.9887 14.6928 15.0073 12.9899C15.1817 12.6985 15.4864 12.5 15.826 12.5C16.4329 12.5002 16.8535 13.102 16.5592 13.6328C15.2801 15.9387 12.8214 17.5 9.99756 17.5C7.38986 17.4998 5.09493 16.1678 3.75162 14.1479L3.61002 14.6639C3.48779 15.1076 3.02832 15.368 2.58463 15.2458C2.14116 15.1234 1.88062 14.6648 2.00276 14.2212L2.66683 11.8107ZM9.7941 7.64079C9.86468 7.45293 10.1311 7.45304 10.2018 7.64079L10.7503 9.12191C10.7738 9.18454 10.8209 9.23232 10.8756 9.24805L12.3568 9.79655C12.5449 9.86708 12.5449 10.1329 12.3568 10.2035L10.8756 10.752C10.813 10.7755 10.766 10.8233 10.7503 10.8781L10.2018 12.3592C10.1311 12.547 9.86468 12.5471 9.7941 12.3592L9.2456 10.8781C9.22209 10.8154 9.17514 10.7676 9.12028 10.752L7.63916 10.2035C7.45133 10.1328 7.45133 9.86717 7.63916 9.79655L9.12028 9.24805C9.18297 9.22454 9.22993 9.17677 9.2456 9.12191L9.7941 7.64079ZM9.99756 2.5C12.8143 2.5 15.2668 4.05374 16.5487 6.3501L16.735 5.91797C16.9165 5.49519 17.4059 5.29974 17.8288 5.48096C18.2516 5.66234 18.4478 6.15182 18.2666 6.57471L17.2811 8.87288C17.111 9.26912 16.6699 9.46607 16.2671 9.34001L16.1865 9.3099L13.8892 8.32438C13.4662 8.14295 13.2699 7.65278 13.4513 7.22982C13.6328 6.80694 14.123 6.61148 14.5459 6.79281L14.9894 6.98324C13.9672 5.29554 12.1147 4.16667 9.99756 4.16667C7.86921 4.16688 6.00792 5.30735 4.98942 7.01009C4.81509 7.30153 4.51033 7.5 4.17073 7.5C3.56366 7.49994 3.14311 6.89808 3.4375 6.36719C4.71651 4.0615 7.17401 2.50022 9.99756 2.5Z"
                    fill="#F7F4EF"
                  />
                </svg>
              </div>

              {/* 3-dot Menu (Rotates on hover) */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: 24,
                  height: 24,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Handle menu action
                }}
              >
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ 
                    transform: isHovered ? "rotate(90deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease"
                  }}
                >
                  <circle cx="3" cy="8" r="1.5" fill="white" fillOpacity="0.9"/>
                  <circle cx="8" cy="8" r="1.5" fill="white" fillOpacity="0.9"/>
                  <circle cx="13" cy="8" r="1.5" fill="white" fillOpacity="0.9"/>
                </svg>
              </div>
            </div>
          )}
        </div>

        {/* Playing state overlay with sound wave - show when playing and not hovered */}
        {isPlaying && !isHovered && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.6)",
              borderRadius: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
            }}
          >
            <SoundWave />
          </div>
        )}

        {/* Hover overlay */}
        {isHovered && (
          <>
            {/* Dark overlay on hover */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.5)",
                borderRadius: 16,
                zIndex: 1,
              }}
            />
            
            {/* Show sound wave if playing, otherwise show play button */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: isPlaying ? "transparent" : "rgba(0,0,0,0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2,
                backdropFilter: isPlaying ? "none" : "blur(10px)"
              }}
            >
              {isPlaying ? (
                <SoundWave />
              ) : (
                <svg
                  width={20}
                  height={20}
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 12.4695V3.5302C4 3.28549 4.09218 3.08036 4.27654 2.91482C4.46091 2.74927 4.67599 2.6665 4.92181 2.6665C4.99863 2.6665 5.07929 2.6773 5.16379 2.69889C5.24829 2.72049 5.32894 2.75287 5.40576 2.79606L12.9185 7.26569C13.0568 7.35206 13.1605 7.46003 13.2296 7.58958C13.2988 7.71914 13.3333 7.85589 13.3333 7.99984C13.3333 8.14379 13.2988 8.28054 13.2296 8.41009C13.1605 8.53965 13.0568 8.64761 12.9185 8.73398L5.40576 13.2036C5.32894 13.2468 5.24829 13.2792 5.16379 13.3008C5.07929 13.3224 4.99863 13.3332 4.92181 13.3332C4.67599 13.3332 4.46091 13.2504 4.27654 13.0849C4.09218 12.9193 4 12.7142 4 12.4695Z"
                    fill="white"
                  />
                </svg>
              )}
            </div>
          </>
        )}
      </div>

      {/* Song info section */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          flexGrow: 0,
          flexShrink: 0,
          height: 68,
          width: size,
          gap: 4,
          paddingLeft: 4,
          paddingRight: 4,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            flexGrow: 0,
            flexShrink: 0,
            position: "relative",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              flexGrow: 0,
              flexShrink: 0,
              position: "relative",
              gap: 8,
            }}
          >
            <p
              style={{
                flexGrow: 0,
                flexShrink: 0,
                fontSize: 16,
                fontWeight: 500,
                textAlign: "left",
                color: "#faf7f5",
              }}
            >
              {song.title}
            </p>
          </div>
          <p style={{ flexGrow: 0, flexShrink: 0, fontSize: 12, textAlign: "left", color: "#d1cbc7" }}>
            {song.genres}
          </p>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            flexGrow: 0,
            flexShrink: 0,
            position: "relative",
            gap: 4,
            borderRadius: 12,
          }}
        >
          <img
            src={song.avatar}
            alt={song.artist}
            style={{
              flexGrow: 0,
              flexShrink: 0,
              width: 12,
              height: 12,
              borderRadius: 999,
              objectFit: "cover",
              borderWidth: "0.5px",
              borderColor: "rgba(255,255,255,0.1)",
              borderStyle: "solid"
            }}
          />
          <p
            style={{
              flexGrow: 0,
              flexShrink: 0,
              width: 80,
              fontSize: 12,
              textAlign: "left",
              color: "#d1cbc7",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {song.artist || 'Neon Relic'}
          </p>
        </div>
      </div>
      
      {/* Remix Submenu - positioned absolute */}
      {showRemixSubmenu && (
        <div 
          ref={submenuRef}
          style={{
            position: "absolute",
            top: "40px", 
            right: "8px",
            zIndex: 999999
          }}
        >
          <RemixSubmenu
            onOpenEditor={() => handleRemixAction('openEditor')}
            onCover={() => handleRemixAction('cover')}
            onKaraoke={() => handleRemixAction('karaoke')}
            onExtend={() => handleRemixAction('extend')}
            onIntro={() => handleRemixAction('intro')}
            onSpeed={() => handleRemixAction('speed')}
            onReplaceSection={() => handleRemixAction('replaceSection')}
            onReuseLyrics={() => handleRemixAction('reuseLyrics')}
            onClose={() => setShowRemixSubmenu(false)}
          />
        </div>
      )}
    </div>
  );
};

const Home = ({
  hasPlaybar = false,
  onPlaySong,
  onPauseSong,
  currentlyPlayingSong,
  onCreateSong,
  onNavigate,
  onNavigateToCreate,
  onCoverSong,
  onKaraokeSong,
  onExtendSong,
  onReplaceSection,
  onReuseLyrics,
  onIntroSong,
  onSpeedSong,
  onSidePanelStateChange,
  onCollapseNavigation,
  isNavCollapsed = false,
  bannersVisible = false,
  onPlaySongInCreate,
  currentSong,
  isPlaying
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [cardsPerRow, setCardsPerRow] = useState(5);
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const [placeholderOpacity, setPlaceholderOpacity] = useState(1);
  const [showAudioDropdown, setShowAudioDropdown] = useState(false);
  const [homeVariant, setHomeVariant] = useState('v1'); // 'v1' shows playlists, 'v2' shows song cards
  const textareaRef = useRef(null);
  const inputSectionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const playlistScrollRef = useRef(null);
  const audioButtonRef = useRef(null);
  const stickyAudioButtonRef = useRef(null);

  // Audio dropdown options
  const audioOptions = [
    {
      value: 'upload',
      label: 'Upload (audio or video)',
      icon: <UploadIcon style={{ width: '16px', height: '16px' }} />
    },
    {
      value: 'record',
      label: 'Record',
      icon: <RecordIcon style={{ width: '16px', height: '16px' }} />
    },
    {
      value: 'library',
      label: 'Library',
      icon: <LibraryIcon style={{ width: '16px', height: '16px' }} />
    }
  ];

  // Example prompts array
  const placeholderPrompts = [
    "a pop birthday song for my best friend Alex",
    "a sad breakup folk song about missing my ex",
    "an upbeat summer anthem for road trips with friends", 
    "a chill lo-fi song for late night studying vibes",
    "a confidence rap song about getting my life together",
    "late night drive vibes with dreamy pop melodies",
    "getting over your ex with confident rap beats",
    "summer golden hour moments with acoustic guitar",
    "3am thoughts about life with moody R&B"
  ];

  // Playlist data for the carousel cards
  const playlistsData = [
    {
      id: 'trending',
      title: 'Trending',
      genres: 'Chill, Electronic, Indie',
      songCount: 31,
      image: '/Album%20Covers/0_0-1.jpg',
      songs: [
        {
          id: 'nocturnal-apparition-playlist',
          title: 'Nocturnal Apparition',
          genres: 'Funk Rock, Neo-Soul, R&B',
          artist: 'Static Halo',
          avatar: 'https://i.pravatar.cc/150?img=1',
          playCount: '2.4k',
          duration: '2:56',
          image: '/Album%20Covers/0_0-1.jpg'
        },
        {
          id: 'neon-echoes-playlist',
          title: 'Neon Echoes',
          genres: 'Blackgaze, Psychedelic Rock',
          artist: 'Vapor Bloom',
          avatar: 'https://i.pravatar.cc/150?img=2',
          playCount: '7.5k',
          duration: '3:24',
          image: '/Album%20Covers/0_0-2.jpg'
        },
        {
          id: 'synthetic-dreams-playlist',
          title: 'Synthetic Dreams',
          genres: 'Sludge Metal, Stoner Rock',
          artist: 'Static Dagger',
          avatar: 'https://i.pravatar.cc/150?img=3',
          playCount: '3.3k',
          duration: '4:12',
          image: '/Album%20Covers/0_0-4.jpg'
        }
      ]
    },
    {
      id: 'bestofv5',
      title: 'Best of v5',
      genres: 'Synthwave, Retro, Dreamy',
      songCount: 28,
      image: '/Album%20Covers/0_0-8.jpg',
      songs: [
        {
          id: 'starlight-highway-playlist',
          title: 'Starlight Highway',
          genres: 'Synthwave, Retro, 80s',
          artist: 'Neon Phoenix',
          avatar: 'https://i.pravatar.cc/150?img=4',
          playCount: '45.6k',
          duration: '5:12',
          image: '/Album%20Covers/0_0-8.jpg'
        },
        {
          id: 'crystal-caves-playlist',
          title: 'Crystal Caves',
          genres: 'Ambient, New Age',
          artist: 'Echo Mountain',
          avatar: 'https://i.pravatar.cc/150?img=5',
          playCount: '12.9k',
          duration: '6:34',
          image: '/Album%20Covers/0_0-9.jpg'
        },
        {
          id: 'tokyo-nights-playlist',
          title: 'Tokyo Nights',
          genres: 'City Pop, Funk, Disco',
          artist: 'Sakura Beats',
          avatar: 'https://i.pravatar.cc/150?img=6',
          playCount: '67.3k',
          duration: '4:45',
          image: '/Album%20Covers/0_0-10.jpg'
        }
      ]
    },
    {
      id: 'featured',
      title: 'Featured',
      genres: 'Orchestral, Cinematic, Ambient',
      songCount: 24,
      image: '/Album%20Covers/0_0-13.jpg',
      songs: [
        {
          id: 'forest-symphony-playlist',
          title: 'Forest Symphony',
          genres: 'Orchestral, Nature, Calm',
          artist: 'Woodland Spirits',
          avatar: 'https://i.pravatar.cc/150?img=7',
          playCount: '38.2k',
          duration: '7:18',
          image: '/Album%20Covers/0_0-13.jpg'
        },
        {
          id: 'neon-pulse-playlist',
          title: 'Neon Pulse',
          genres: 'Drum & Bass, Cyber',
          artist: 'Digital Rain',
          avatar: 'https://i.pravatar.cc/150?img=8',
          playCount: '52.8k',
          duration: '3:42',
          image: '/Album%20Covers/0_0.jpg'
        },
        {
          id: 'desert-wind-playlist',
          title: 'Desert Wind',
          genres: 'World, Ethnic, Meditative',
          artist: 'Sahara Moon',
          avatar: 'https://i.pravatar.cc/150?img=9',
          playCount: '24.6k',
          duration: '5:33',
          image: '/Album%20Covers/0_1-1.jpg'
        }
      ]
    },
    {
      id: 'newreleases',
      title: 'New Releases',
      genres: 'Pop, Rock, Electronic',
      songCount: 15,
      image: '/Album%20Covers/0_3.jpg',
      songs: [
        {
          id: 'digital-rain-playlist',
          title: 'Digital Rain',
          genres: 'Cyber, Electronic',
          artist: 'Future Echo',
          avatar: 'https://i.pravatar.cc/150?img=10',
          playCount: '15.2k',
          duration: '4:28',
          image: '/Album%20Covers/0_3.jpg'
        },
        {
          id: 'velvet-dreams-playlist',
          title: 'Velvet Dreams',
          genres: 'R&B, Soul, Smooth',
          artist: 'Midnight Groove',
          avatar: 'https://i.pravatar.cc/150?img=11',
          playCount: '19.7k',
          duration: '3:55',
          image: '/Album%20Covers/0_2-1.jpg'
        },
        {
          id: 'electric-storm-playlist',
          title: 'Electric Storm',
          genres: 'EDM, Bass, Future',
          artist: 'Volt Collective',
          avatar: 'https://i.pravatar.cc/150?img=12',
          playCount: '31.8k',
          duration: '6:12',
          image: '/Album%20Covers/0_3-1.jpg'
        }
      ]
    }
  ];

  // Mock song data for the new card layout
  const trendingSongs = [
    {
      id: 1,
      title: "Ghosts of Tomorrow",
      genres: "Rap, Hip Hop, Funk",
      artist: "Neon Relic",
      image: "/Album%20Covers/0_0-1.jpg",
      avatar: "https://i.pravatar.cc/150?img=1",
      playCount: "13.3k"
    },
    {
      id: 2,
      title: "Digital Sunrise",
      genres: "Electronic, Synthwave",
      artist: "Chrome Valley",
      image: "/Album%20Covers/0_0-2.jpg",
      avatar: "https://i.pravatar.cc/150?img=2",
      playCount: "8.7k"
    },
    {
      id: 3,
      title: "Midnight Express",
      genres: "Jazz, Lo-fi, Chill",
      artist: "Luna Blue",
      image: "/Album%20Covers/0_0-4.jpg",
      avatar: "https://i.pravatar.cc/150?img=3",
      playCount: "22.1k"
    },
    {
      id: 4,
      title: "Ocean Drive",
      genres: "Pop, Indie, Dreamy",
      artist: "Coastal Dreams",
      image: "/Album%20Covers/0_0-5.jpg",
      avatar: "https://i.pravatar.cc/150?img=4",
      playCount: "15.2k"
    },
    {
      id: 5,
      title: "Electric Storm",
      genres: "EDM, Bass, Future",
      artist: "Volt Collective",
      image: "/Album%20Covers/0_0-7.jpg",
      avatar: "https://i.pravatar.cc/150?img=5",
      playCount: "31.8k"
    }
  ];

  const bestOfV5Songs = [
    {
      id: 6,
      title: "Starlight Highway",
      genres: "Synthwave, Retro, 80s",
      artist: "Neon Phoenix",
      image: "/Album%20Covers/0_0-8.jpg",
      avatar: "https://i.pravatar.cc/150?img=6",
      playCount: "45.6k"
    },
    {
      id: 7,
      title: "Crystal Caves",
      genres: "Ambient, New Age",
      artist: "Echo Mountain",
      image: "/Album%20Covers/0_0-9.jpg",
      avatar: "https://i.pravatar.cc/150?img=7",
      playCount: "12.9k"
    },
    {
      id: 8,
      title: "Tokyo Nights",
      genres: "City Pop, Funk, Disco",
      artist: "Sakura Beats",
      image: "/Album%20Covers/0_0-10.jpg",
      avatar: "https://i.pravatar.cc/150?img=8",
      playCount: "67.3k"
    },
    {
      id: 9,
      title: "Quantum Leap",
      genres: "Psytrance, Electronic",
      artist: "Cosmic Wanderer",
      image: "/Album%20Covers/0_0-11.jpg",
      avatar: "https://i.pravatar.cc/150?img=9",
      playCount: "28.4k"
    },
    {
      id: 10,
      title: "Velvet Dreams",
      genres: "R&B, Soul, Smooth",
      artist: "Midnight Groove",
      image: "/Album%20Covers/0_0-12.jpg",
      avatar: "https://i.pravatar.cc/150?img=10",
      playCount: "19.7k"
    }
  ];

  const featuredSongs = [
    {
      id: 11,
      title: "Forest Symphony",
      genres: "Orchestral, Nature, Calm",
      artist: "Woodland Spirits",
      image: "/Album%20Covers/0_0-13.jpg",
      avatar: "https://i.pravatar.cc/150?img=11",
      playCount: "38.2k"
    },
    {
      id: 12,
      title: "Neon Pulse",
      genres: "Drum & Bass, Cyber",
      artist: "Digital Rain",
      image: "/Album%20Covers/0_0.jpg",
      avatar: "https://i.pravatar.cc/150?img=12",
      playCount: "52.8k"
    },
    {
      id: 13,
      title: "Desert Wind",
      genres: "World, Ethnic, Meditative",
      artist: "Sahara Moon",
      image: "/Album%20Covers/0_1-1.jpg",
      avatar: "https://i.pravatar.cc/150?img=13",
      playCount: "24.6k"
    },
    {
      id: 14,
      title: "Crimson Sky",
      genres: "Post-Rock, Cinematic",
      artist: "Horizon Fire",
      image: "/Album%20Covers/0_1.jpg",
      avatar: "https://i.pravatar.cc/150?img=14",
      playCount: "41.9k"
    },
    {
      id: 15,
      title: "Midnight Café",
      genres: "Jazz, Lounge, Smooth",
      artist: "Blue Note Society",
      image: "/Album%20Covers/0_2-1.jpg",
      avatar: "https://i.pravatar.cc/150?img=15",
      playCount: "17.4k"
    }
  ];

  // Calculate responsive card sizing
  const [cardSize, setCardSize] = useState(200);
  
  useEffect(() => {
    const calculateCardSize = () => {
      const navWidth = isNavCollapsed ? 80 : 240;
      const padding = 48; // 24px on each side
      const minRowWidth = 696; // Same as input width
      const gap = 16;
      const cardsCount = 5; // Always show 5 cards
      
      const availableWidth = window.innerWidth - navWidth - padding;
      const targetWidth = Math.max(availableWidth, minRowWidth);
      
      // Calculate card size: (total width - gaps) / number of cards
      const calculatedCardSize = Math.floor((targetWidth - (gap * (cardsCount - 1))) / cardsCount);
      const finalCardSize = Math.min(200, Math.max(120, calculatedCardSize)); // Min 120px, max 200px
      
      setCardSize(finalCardSize);
    };

    calculateCardSize();
    window.addEventListener('resize', calculateCardSize);
    return () => window.removeEventListener('resize', calculateCardSize);
  }, [isNavCollapsed]);

  // Scroll detection for sticky input
  useEffect(() => {
    const handleScroll = () => {
      if (inputSectionRef.current) {
        const inputRect = inputSectionRef.current.getBoundingClientRect();
        const shouldBeSticky = inputRect.bottom < 100; // Trigger when input is 100px from top
        console.log('Scroll event:', { inputRect: inputRect.bottom, shouldBeSticky });
        setIsSticky(shouldBeSticky);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      console.log('Scroll listener attached to:', scrollContainer);
      scrollContainer.addEventListener('scroll', handleScroll);
      // Also trigger initial check
      handleScroll();
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    } else {
      console.log('No scroll container found');
    }
  }, []);

  // Cycling placeholder animation
  useEffect(() => {
    if (inputValue) return; // Don't cycle if user has typed something

    const interval = setInterval(() => {
      setPlaceholderOpacity(0); // Fade out
      
      setTimeout(() => {
        setCurrentPlaceholderIndex((prev) => 
          (prev + 1) % placeholderPrompts.length
        );
        setPlaceholderOpacity(1); // Fade in
      }, 300); // Wait for fade out to complete
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, [inputValue, placeholderPrompts.length]);

  // Check for playlist overflow on mount
  useEffect(() => {
    const checkPlaylistOverflow = () => {
      if (playlistScrollRef.current) {
        const container = playlistScrollRef.current;
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;
        const hasOverflow = scrollWidth > clientWidth;
        
        console.log('Initial overflow check:', { scrollWidth, clientWidth, hasOverflow });
        
        const rightGradient = document.querySelector('.playlist-right-gradient');
        if (rightGradient) {
          rightGradient.style.opacity = hasOverflow ? '1' : '0';
        }
      }
    };

    // Check after component mounts and cards are rendered
    setTimeout(checkPlaylistOverflow, 100);
  }, []);

  const handleGenerate = async () => {
    if (!inputValue.trim() || isGenerating) return;
    
    setIsGenerating(true);
    
    // Navigate to create tab with the input data
    onNavigateToCreate?.({
      description: inputValue,
      mode: "Simple",
      title: "Untitled",
      genres: []
    });
    
    // Reset input
    setInputValue('');
    
    // Reset generating state after navigation
    setTimeout(() => {
      setIsGenerating(false);
    }, 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const handleSongPlay = (song) => {
    onPlaySong?.(song);
  };

  const handleSongPause = () => {
    onPauseSong?.();
  };

  const handleAudioButtonClick = () => {
    setShowAudioDropdown(!showAudioDropdown);
  };

  const handleAudioOptionSelect = (value) => {
    setShowAudioDropdown(false);
    
    switch (value) {
      case 'upload':
        // TODO: Implement upload functionality
        console.log('Upload clicked');
        break;
      case 'record':
        // TODO: Implement record functionality
        console.log('Record clicked');
        break;
      case 'library':
        // TODO: Implement library functionality
        console.log('Library clicked');
        break;
    }
  };

  const handleRemix = (action, song) => {
    console.log(`Home: Handling remix action ${action} for song:`, song);
    
    // Convert song data to the format expected by the remix handlers
    const songData = {
      id: song.id,
      title: song.title,
      image: song.image,
      duration: '3:15', // Mock duration since home songs don't have this
      genres: song.genres
    };

    switch (action) {
      case 'cover':
        onCoverSong?.(songData);
        break;
      case 'karaoke':
        onKaraokeSong?.(songData);
        break;
      case 'extend':
        onExtendSong?.(songData);
        break;
      case 'replaceSection':
        onReplaceSection?.(songData);
        break;
      case 'reuseLyrics':
        onReuseLyrics?.(songData);
        break;
      case 'intro':
        onIntroSong?.(songData);
        break;
      case 'speed':
        onSpeedSong?.(songData);
        break;
      case 'openEditor':
        // Navigate to create with custom mode
        onNavigate?.('create', { mode: 'Custom' });
        break;
      default:
        console.log('Unknown remix action:', action);
    }
  };

  return (
    <>
      <style>{`
        .home-input::placeholder {
          color: rgba(255,255,255,0.3);
          transition: opacity 0.3s ease-in-out;
        }
        .home-input::selection {
          background-color: ${colors.accent};
          color: ${colors.text.primary};
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        @media (max-width: 768px) {
          .song-grid {
            grid-template-columns: 1fr;
          }
        }
        
        @keyframes soundWave1 {
          0%, 40%, 100% { height: 16px; }
          20% { height: 8px; }
        }
        @keyframes soundWave2 {
          0%, 20%, 100% { height: 12px; }
          10% { height: 20px; }
        }
        @keyframes soundWave3 {
          0%, 80%, 100% { height: 20px; }
          40% { height: 8px; }
        }
        @keyframes soundWave4 {
          0%, 60%, 100% { height: 14px; }
          30% { height: 18px; }
        }
      `}</style>
      
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-auto"
        style={{ 
          backgroundColor: colors.bg.primary,
          paddingBottom: hasPlaybar ? '80px' : '0'
        }}
      >
        {/* Header and Input Section */}
        <div className="flex flex-col items-center w-full pb-8 relative" style={{ gap: '24px', paddingTop: '140px' }}>
          {/* Search Button - Top Right */}
          <div className="absolute top-6 right-6 z-10">
            <div 
              className="flex justify-center items-center h-11 relative gap-2 px-4 rounded-[100px] cursor-pointer hover:bg-white/[0.08] transition-colors"
              style={{ 
                background: 'rgba(255,255,255,0.04)', 
                border: '0.5px solid rgba(255,255,255,0.1)',
                backdropFilter: 'blur(20px)'
              }}
              onClick={() => setHomeVariant(homeVariant === 'v1' ? 'v2' : 'v1')}
            >
              <svg
                width={16}
                height={16}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-grow-0 flex-shrink-0 w-4 h-4 relative"
                preserveAspectRatio="xMidYMid meet"
              >
                <path
                  d="M10.4168 17.8336C8.3439 17.8336 6.58954 17.1157 5.15372 15.6799C3.71791 14.2441 3 12.4897 3 10.4168C3 8.3439 3.71791 6.58954 5.15372 5.15372C6.58954 3.71791 8.3439 3 10.4168 3C12.4897 3 14.2441 3.71791 15.6799 5.15372C17.1157 6.58954 17.8336 8.3439 17.8336 10.4168C17.8336 11.2536 17.7005 12.0428 17.4342 12.7845C17.168 13.5261 16.8067 14.1823 16.3502 14.7528L20.6862 19.0887C20.8954 19.2979 21 19.5642 21 19.8875C21 20.2108 20.8954 20.477 20.6862 20.6862C20.477 20.8954 20.2108 21 19.8875 21C19.5642 21 19.2979 20.8954 19.0887 20.6862L14.7528 16.3502C14.1823 16.8067 13.5261 17.168 12.7845 17.4342C12.0428 17.7005 11.2536 17.8336 10.4168 17.8336ZM10.4168 15.5515C11.8431 15.5515 13.0555 15.0523 14.0539 14.0539C15.0523 13.0555 15.5515 11.8431 15.5515 10.4168C15.5515 8.99049 15.0523 7.77813 14.0539 6.77971C13.0555 5.7813 11.8431 5.28209 10.4168 5.28209C8.99049 5.28209 7.77813 5.7813 6.77971 6.77971C5.7813 7.77813 5.28209 8.99049 5.28209 10.4168C5.28209 11.8431 5.7813 13.0555 6.77971 14.0539C7.77813 15.0523 8.99049 15.5515 10.4168 15.5515Z"
                  fill="white"
                />
              </svg>
              <p 
                className="flex-grow-0 flex-shrink-0 font-medium text-left"
                style={{ 
                  fontFamily: 'PP Neue Montreal',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: colors.text.inactive
                }}
              >
                Search
              </p>
            </div>
          </div>
          {/* Background Gradient */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '800px',
              height: '400px',
              background: 'radial-gradient(ellipse at center, #281D2F 0%, transparent 70%)',
              zIndex: 0,
              opacity: 1,
              pointerEvents: 'none'
            }}
          />
          
          {/* Header */}
          <div className="flex flex-col items-start w-full max-w-[696px] relative" style={{ gap: '8px', zIndex: 1 }}>
            <p 
              className="text-base text-center w-full"
              style={{ color: '#a3a3a3' }}
            >
              Let's get started
            </p>
            <p 
              className="text-5xl font-medium text-center w-full text-white"
              style={{ fontSize: '40px', fontWeight: 500 }}
            >
              Make any song you can imagine
            </p>
          </div>

          {/* Chat Input Section */}
          <div 
            ref={inputSectionRef}
            className="flex flex-col items-start w-full max-w-[696px] relative" 
            style={{ borderRadius: '21px', zIndex: 1 }}
          >
            <div 
              className="flex flex-col items-start w-full"
              style={{ 
                background: 'rgba(255,255,255,0.04)', 
                borderRadius: '24px',
                border: '0.5px solid rgba(255,255,255,0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}
            >
              {/* First Row - Text Input */}
              <div className="flex items-start w-full px-4 pt-4 relative">
                <div className="relative w-full">
                  <input
                    ref={textareaRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder=""
                    className="home-input w-full bg-transparent border-none outline-none text-base font-medium"
                    style={{ 
                      fontFamily: 'PP Neue Montreal',
                      fontWeight: 500,
                      color: colors.text.secondary,
                      caretColor: colors.accent
                    }}
                  />
                  {/* Cycling Placeholder */}
                  {!inputValue && (
                    <div
                      className="absolute top-0 left-0 w-full h-full flex items-center pointer-events-none text-base font-medium"
                      style={{ 
                        fontFamily: 'PP Neue Montreal',
                        fontWeight: 500,
                        color: 'rgba(255,255,255,0.3)',
                        opacity: placeholderOpacity,
                        transition: 'opacity 0.3s ease-in-out'
                      }}
                    >
                      {placeholderPrompts[currentPlaceholderIndex]}
                    </div>
                  )}
                </div>
              </div>

              {/* Second Row - Controls */}
              <div className="flex justify-between items-center w-full px-4 py-3">
                <div className="flex items-center gap-2">
                  {/* Plus Icon Button */}
                  <div 
                    ref={audioButtonRef}
                    className="flex items-center justify-center w-10 h-10 rounded-full cursor-pointer transition-colors"
                    style={{ background: 'rgba(255,255,255,0.04)' }}
                    onClick={handleAudioButtonClick}
                  >
                    <svg
                      width={16}
                      height={16}
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 2C7.52661 2 7.14286 2.38376 7.14286 2.85714V7.14286L2.85714 7.14286C2.38376 7.14286 2 7.52661 2 8C2 8.47339 2.38376 8.85714 2.85714 8.85714H7.14286V13.1429C7.14286 13.6162 7.52661 14 8 14C8.47339 14 8.85714 13.6162 8.85714 13.1429V8.85714H13.1429C13.6162 8.85714 14 8.47339 14 8C14 7.52661 13.6162 7.14286 13.1429 7.14286L8.85714 7.14286V2.85714C8.85714 2.38376 8.47339 2 8 2Z"
                        fill="#F7F4EF"
                      />
                    </svg>
                  </div>

                  {/* Sliders Icon Button */}
                  <div 
                    className="flex items-center justify-center w-10 h-10 rounded-full cursor-pointer transition-colors"
                    style={{ background: 'rgba(255,255,255,0.04)' }}
                    onClick={() => onNavigate?.('create', { mode: 'Custom' })}
                  >
                    <svg
                      width={16}
                      height={16}
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 13.3332C3.81111 13.3332 3.65278 13.2693 3.525 13.1415C3.39722 13.0137 3.33333 12.8554 3.33333 12.6665V8.6665H2.66667C2.47778 8.6665 2.31944 8.60262 2.19167 8.47484C2.06389 8.34706 2 8.18873 2 7.99984C2 7.81095 2.06389 7.65261 2.19167 7.52484C2.31944 7.39706 2.47778 7.33317 2.66667 7.33317H5.33333C5.52222 7.33317 5.68056 7.39706 5.80833 7.52484C5.93611 7.65261 6 7.81095 6 7.99984C6 8.18873 5.93611 8.34706 5.80833 8.47484C5.68056 8.60262 5.52222 8.6665 5.33333 8.6665H4.66667V12.6665C4.66667 12.8554 4.60278 13.0137 4.475 13.1415C4.34722 13.2693 4.18889 13.3332 4 13.3332ZM4 5.99984C3.81111 5.99984 3.65278 5.93595 3.525 5.80817C3.39722 5.68039 3.33333 5.52206 3.33333 5.33317V3.33317C3.33333 3.14428 3.39722 2.98595 3.525 2.85817C3.65278 2.73039 3.81111 2.6665 4 2.6665C4.18889 2.6665 4.34722 2.73039 4.475 2.85817C4.60278 2.98595 4.66667 3.14428 4.66667 3.33317V5.33317C4.66667 5.52206 4.60278 5.68039 4.475 5.80817C4.34722 5.93595 4.18889 5.99984 4 5.99984ZM6.66667 5.99984C6.47778 5.99984 6.31944 5.93595 6.19167 5.80817C6.06389 5.68039 6 5.52206 6 5.33317C6 5.14428 6.06389 4.98595 6.19167 4.85817C6.31944 4.73039 6.47778 4.6665 6.66667 4.6665H7.33333V3.33317C7.33333 3.14428 7.39722 2.98595 7.525 2.85817C7.65278 2.73039 7.81111 2.6665 8 2.6665C8.18889 2.6665 8.34722 2.73039 8.475 2.85817C8.60278 2.98595 8.66667 3.14428 8.66667 3.33317V4.6665H9.33333C9.52222 4.6665 9.68056 4.73039 9.80833 4.85817C9.93611 4.98595 10 5.14428 10 5.33317C10 5.52206 9.93611 5.68039 9.80833 5.80817C9.68056 5.93595 9.52222 5.99984 9.33333 5.99984H6.66667ZM8 13.3332C7.81111 13.3332 7.65278 13.2693 7.525 13.1415C7.39722 13.0137 7.33333 12.8554 7.33333 12.6665V7.99984C7.33333 7.81095 7.39722 7.65261 7.525 7.52484C7.65278 7.39706 7.81111 7.33317 8 7.33317C8.18889 7.33317 8.34722 7.39706 8.475 7.52484C8.60278 7.65261 8.66667 7.81095 8.66667 7.99984V12.6665C8.66667 12.8554 8.60278 13.0137 8.475 13.1415C8.34722 13.2693 8.18889 13.3332 8 13.3332ZM12 13.3332C11.8111 13.3332 11.6528 13.2693 11.525 13.1415C11.3972 13.0137 11.3333 12.8554 11.3333 12.6665V11.3332H10.6667C10.4778 11.3332 10.3194 11.2693 10.1917 11.1415C10.0639 11.0137 10 10.8554 10 10.6665C10 10.4776 10.0639 10.3193 10.1917 10.1915C10.3194 10.0637 10.4778 9.99984 10.6667 9.99984H13.3333C13.5222 9.99984 13.6806 10.0637 13.8083 10.1915C13.9361 10.3193 14 10.4776 14 10.6665C14 10.8554 13.9361 11.0137 13.8083 11.1415C13.6806 11.2693 13.5222 11.3332 13.3333 11.3332H12.6667V12.6665C12.6667 12.8554 12.6028 13.0137 12.475 13.1415C12.3472 13.2693 12.1889 13.3332 12 13.3332ZM12 8.6665C11.8111 8.6665 11.6528 8.60262 11.525 8.47484C11.3972 8.34706 11.3333 8.18873 11.3333 7.99984V3.33317C11.3333 3.14428 11.3972 2.98595 11.525 2.85817C11.6528 2.73039 11.8111 2.6665 12 2.6665C12.1889 2.6665 12.3472 2.73039 12.475 2.85817C12.6028 2.98595 12.6667 3.14428 12.6667 3.33317V7.99984C12.6667 8.18873 12.6028 8.34706 12.475 8.47484C12.3472 8.60262 12.1889 8.6665 12 8.6665Z"
                        fill="#F7F4EF"
                      />
                    </svg>
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={!inputValue.trim() || isGenerating}
                  className={`flex items-center justify-center transition-all duration-200 ${
                    inputValue.trim() ? 'w-8 h-8 rounded-full' : 'w-6 h-6'
                  }`}
                  style={{
                    backgroundImage: inputValue.trim() ? "url('/aura.png')" : 'none',
                    backgroundSize: inputValue.trim() ? "cover" : 'auto',
                    backgroundPosition: inputValue.trim() ? "center" : 'initial',
                    backgroundRepeat: inputValue.trim() ? "no-repeat" : 'repeat',
                    opacity: inputValue.trim() ? 1 : 0.3
                  }}
                >
                  {isGenerating ? (
                    <SmallSpinner />
                  ) : inputValue.trim() ? (
                    // Checkmark icon
                    <svg
                      width={16}
                      height={16}
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.3334 4.66663L6.00008 12L2.66675 8.66663"
                        stroke="white"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    // Microphone icon
                    <svg
                      width={24}
                      height={24}
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.9472 14C11.1172 14 10.4072 13.71 9.8272 13.12C9.2472 12.54 8.9472 11.83 8.9472 11V5C8.9472 4.17 9.2372 3.46 9.8272 2.88C10.4072 2.3 11.1172 2 11.9472 2C12.7772 2 13.4872 2.29 14.0672 2.88C14.6472 3.47 14.9472 4.17 14.9472 5V11C14.9472 11.83 14.6572 12.54 14.0672 13.12C13.4772 13.7 12.7772 14 11.9472 14ZM11.9472 21C11.3972 21 10.9472 20.55 10.9472 20V18.8C10.9472 18.3 10.5772 17.88 10.0872 17.76C8.7372 17.41 7.5872 16.69 6.6472 15.6C5.7372 14.54 5.1872 13.34 5.0072 12C4.9372 11.45 5.3872 11 5.9472 11C6.4972 11 6.9372 11.45 7.0372 11.99C7.2172 12.95 7.6772 13.8 8.4072 14.53C9.3872 15.5 10.5572 15.99 11.9472 15.99C13.3372 15.99 14.5072 15.5 15.4872 14.53C16.2172 13.8 16.6772 12.95 16.8572 11.99C16.9572 11.45 17.3972 11 17.9472 11C18.4972 11 18.9572 11.45 18.8872 12C18.7072 13.34 18.1672 14.54 17.2472 15.6C16.3072 16.69 15.1572 17.41 13.8072 17.76C13.3172 17.88 12.9472 18.3 12.9472 18.8V20C12.9472 20.55 12.4972 21 11.9472 21Z"
                        fill="white"
                        fillOpacity="0.3"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Promotional Banners */}
        {bannersVisible && (
          <div className="px-6 pb-6">
            <div className="grid grid-cols-2 gap-4">
            {/* Banner 1 */}
            <div
              className="flex flex-col justify-between items-start flex-grow h-[188px] overflow-hidden p-3 rounded-[20px]"
              style={{ 
                backgroundImage: `linear-gradient(to bottom, rgba(14,8,8,0) -4.94%, rgba(14,8,8,0.7) 100%), url('/Album Covers/studio.png')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat"
              }}
            >
              <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 gap-2">
                <div className="flex justify-center items-center flex-grow-0 flex-shrink-0 h-[22px] relative px-2 rounded-[13.38px] bg-[#f5d907]">
                  <p className="flex-grow-0 flex-shrink-0 text-[10px] font-medium text-left text-[#101012]">
                    NEW PRODUCT
                  </p>
                </div>
              </div>
              <div className="flex flex-col justify-start items-start flex-grow-0 flex-shrink-0 gap-3">
                <div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 relative gap-1">
                  <p className="self-stretch flex-grow-0 flex-shrink-0 text-lg sm:text-2xl font-semibold text-left text-white leading-none">
                    Suno Studio is here
                  </p>
                  <p className="self-stretch flex-grow-0 flex-shrink-0 text-xs font-medium text-left text-white">
                    Unlock your full creative potential with the first-ever generative audio workstation.{" "}
                  </p>
                </div>
                <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 gap-2">
                  <div className="flex justify-center items-center flex-grow-0 flex-shrink-0 h-8 relative gap-1 px-4 rounded-[100px] bg-white">
                    <p className="flex-grow-0 flex-shrink-0 text-xs font-medium text-left text-black whitespace-nowrap">Try Now</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Banner 2 */}
            <div
              className="flex flex-col justify-between items-start flex-grow h-[188px] overflow-hidden p-3 rounded-[20px]"
              style={{ 
                backgroundImage: `linear-gradient(to bottom, rgba(14,8,8,0) -4.94%, rgba(14,8,8,0.7) 100%), url('/Album Covers/v5.png')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat"
              }}
            >
              <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 gap-2">
                <div className="flex justify-center items-center flex-grow-0 flex-shrink-0 h-[22px] relative px-2 rounded-[13.38px] bg-[#e91e63]">
                  <p className="flex-grow-0 flex-shrink-0 text-[10px] font-medium text-left text-white">
                    NEW MODEL
                  </p>
                </div>
                <div className="flex justify-center items-center flex-grow-0 flex-shrink-0 h-[22px] relative px-2 rounded-[13.38px] border border-white">
                  <p className="flex-grow-0 flex-shrink-0 text-[10px] font-medium text-left text-white">
                    BETA
                  </p>
                </div>
              </div>
              <div className="flex flex-col justify-start items-start flex-grow-0 flex-shrink-0 gap-3">
                <div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 relative gap-1">
                  <p className="self-stretch flex-grow-0 flex-shrink-0 text-lg sm:text-2xl font-semibold text-left text-white leading-none">
                    The world's best music model
                  </p>
                  <p className="self-stretch flex-grow-0 flex-shrink-0 text-xs font-medium text-left text-white">
                    Authentic vocals, unparalleled control, studio-quality results.{" "}
                  </p>
                </div>
                <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 gap-2">
                  <div className="flex justify-center items-center flex-grow-0 flex-shrink-0 h-8 relative gap-1 px-4 rounded-[100px] bg-white">
                    <p className="flex-grow-0 flex-shrink-0 text-xs font-medium text-left text-black whitespace-nowrap">Try Now</p>
                  </div>
                  <div className="flex justify-center items-center flex-grow-0 flex-shrink-0 h-8 relative gap-1 px-4 rounded-[100px] bg-white/[0.04] border border-white/10 backdrop-blur-[94px]">
                    <p className="flex-grow-0 flex-shrink-0 text-xs font-medium text-left text-white whitespace-nowrap">Learn More</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Get Inspired Playlist Carousel - Only show in v1 */}
        {homeVariant === 'v1' && (
          <div className="pr-6 pb-8 relative">
            <div className="flex flex-col items-center px-6">
              {/* Section Header */}
              <div className="flex justify-between items-center mb-3 w-full">
                <h2 className="text-xl font-medium text-white">Get Inspired</h2>
              </div>

              {/* Horizontal Scrolling Container with gradients */}
              <div className="relative w-full">
                <div 
                  className="w-full overflow-x-auto scrollbar-hide"
                  ref={playlistScrollRef}
                  onScroll={(e) => {
                    const container = e.target;
                    const scrollLeft = container.scrollLeft;
                    const scrollWidth = container.scrollWidth;
                    const clientWidth = container.clientWidth;
                    
                    console.log('Scroll info:', { scrollLeft, scrollWidth, clientWidth, hasOverflow: scrollWidth > clientWidth });
                    
                    // Show left gradient when scrolled right
                    const leftGradient = document.querySelector('.playlist-left-gradient');
                    if (leftGradient) {
                      leftGradient.style.opacity = scrollLeft > 0 ? '1' : '0';
                    }
                    
                    // Show right gradient when there's content to scroll
                    const rightGradient = document.querySelector('.playlist-right-gradient');
                    if (rightGradient) {
                      const hasMoreContent = scrollLeft < scrollWidth - clientWidth - 1;
                      rightGradient.style.opacity = hasMoreContent ? '1' : '0';
                      console.log('Right gradient:', { hasMoreContent, opacity: rightGradient.style.opacity });
                    }
                  }}
                >
                  <div className="flex gap-4 pb-2">
                    {playlistsData.map((playlist) => (
                      <PlaylistCard
                        key={playlist.id}
                        playlist={playlist}
                        onRemixClick={handleRemix}
                        onPlaySong={handleSongPlay}
                        currentSong={currentlyPlayingSong}
                        isPlaying={currentlyPlayingSong?.id ? true : false}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Left fade gradient - positioned at container edge */}
                <div 
                  className="playlist-left-gradient absolute left-0 top-0 w-12 h-full pointer-events-none z-10 transition-opacity duration-200"
                  style={{
                    background: `linear-gradient(to right, ${colors.bg.primary}, transparent)`,
                    opacity: 0
                  }}
                />
                
                {/* Right fade gradient - positioned at container edge */}
                <div 
                  className="playlist-right-gradient absolute right-0 top-0 w-12 h-full pointer-events-none z-10 transition-opacity duration-200"
                  style={{
                    background: `linear-gradient(to left, ${colors.bg.primary}, transparent)`,
                    opacity: 1
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Song Sections - Only show in v2 */}
        {homeVariant === 'v2' && (
          <div className="px-6 pb-6 space-y-8">
          {/* Trending on Suno Section */}
          <div className="flex flex-col items-center">
            <div 
              className="flex justify-between items-center mb-3"
              style={{
                width: `${cardSize * 5 + 16 * 4}px` // 5 cards + 4 gaps
              }}
            >
              <h2 className="text-xl font-medium text-white">
                Trending on Suno
              </h2>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexGrow: 0,
                  flexShrink: 0,
                  height: 32,
                  position: "relative",
                  paddingLeft: 12,
                  paddingRight: 8,
                  paddingTop: 4,
                  paddingBottom: 4,
                  borderRadius: 16,
                  background: "rgba(255,255,255,0.04)",
                  cursor: "pointer"
                }}
              >
                <p
                  style={{
                    flexGrow: 0,
                    flexShrink: 0,
                    fontSize: 14,
                    fontWeight: 500,
                    textAlign: "left",
                    color: "rgba(255,255,255,0.6)",
                  }}
                >
                  See All
                </p>
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexGrow: 0, flexShrink: 0, width: 16, height: 16, position: "relative" }}
                  preserveAspectRatio="xMidYMid meet"
                >
                  <path
                    d="M9.73515 8.00323L5.60621 3.87429C5.46577 3.73385 5.39789 3.56766 5.40257 3.37573C5.40725 3.18379 5.47981 3.0176 5.62025 2.87716C5.76069 2.73672 5.92688 2.6665 6.11882 2.6665C6.31075 2.6665 6.47694 2.73672 6.61738 2.87716L10.9289 7.20273C11.0412 7.31508 11.1255 7.44147 11.1817 7.58191C11.2379 7.72235 11.266 7.86279 11.266 8.00323C11.266 8.14368 11.2379 8.28412 11.1817 8.42456C11.1255 8.565 11.0412 8.69139 10.9289 8.80374L6.60333 13.1293C6.46289 13.2697 6.29905 13.3376 6.11179 13.3329C5.92454 13.3283 5.76069 13.2557 5.62025 13.1153C5.47981 12.9748 5.40959 12.8086 5.40959 12.6167C5.40959 12.4248 5.47981 12.2586 5.62025 12.1181L9.73515 8.00323Z"
                    fill="white"
                    fillOpacity="0.6"
                  />
                </svg>
              </div>
            </div>
            <div 
              className="grid gap-4"
              style={{
                gridTemplateColumns: `repeat(5, ${cardSize}px)`,
                justifyContent: 'center'
              }}
            >
              {trendingSongs.map((song) => (
                <SongCard
                  key={song.id}
                  song={song}
                  size={cardSize}
                  isPlaying={currentlyPlayingSong?.id === song.id}
                  onPlay={handleSongPlay}
                  onPause={handleSongPause}
                  onRemix={handleRemix}
                />
              ))}
            </div>
          </div>

          {/* Best of v5 Section */}
          <div className="flex flex-col items-center">
            <div 
              className="flex justify-between items-center mb-3"
              style={{
                width: `${cardSize * 5 + 16 * 4}px` // 5 cards + 4 gaps
              }}
            >
              <h2 className="text-xl font-medium text-white">
                Best of v5
              </h2>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexGrow: 0,
                  flexShrink: 0,
                  height: 32,
                  position: "relative",
                  paddingLeft: 12,
                  paddingRight: 8,
                  paddingTop: 4,
                  paddingBottom: 4,
                  borderRadius: 16,
                  background: "rgba(255,255,255,0.04)",
                  cursor: "pointer"
                }}
              >
                <p
                  style={{
                    flexGrow: 0,
                    flexShrink: 0,
                    fontSize: 14,
                    fontWeight: 500,
                    textAlign: "left",
                    color: "rgba(255,255,255,0.6)",
                  }}
                >
                  See All
                </p>
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexGrow: 0, flexShrink: 0, width: 16, height: 16, position: "relative" }}
                  preserveAspectRatio="xMidYMid meet"
                >
                  <path
                    d="M9.73515 8.00323L5.60621 3.87429C5.46577 3.73385 5.39789 3.56766 5.40257 3.37573C5.40725 3.18379 5.47981 3.0176 5.62025 2.87716C5.76069 2.73672 5.92688 2.6665 6.11882 2.6665C6.31075 2.6665 6.47694 2.73672 6.61738 2.87716L10.9289 7.20273C11.0412 7.31508 11.1255 7.44147 11.1817 7.58191C11.2379 7.72235 11.266 7.86279 11.266 8.00323C11.266 8.14368 11.2379 8.28412 11.1817 8.42456C11.1255 8.565 11.0412 8.69139 10.9289 8.80374L6.60333 13.1293C6.46289 13.2697 6.29905 13.3376 6.11179 13.3329C5.92454 13.3283 5.76069 13.2557 5.62025 13.1153C5.47981 12.9748 5.40959 12.8086 5.40959 12.6167C5.40959 12.4248 5.47981 12.2586 5.62025 12.1181L9.73515 8.00323Z"
                    fill="white"
                    fillOpacity="0.6"
                  />
                </svg>
              </div>
            </div>
            <div 
              className="grid gap-4"
              style={{
                gridTemplateColumns: `repeat(5, ${cardSize}px)`,
                justifyContent: 'center'
              }}
            >
              {bestOfV5Songs.map((song) => (
                <SongCard
                  key={song.id}
                  song={song}
                  size={cardSize}
                  isPlaying={currentlyPlayingSong?.id === song.id}
                  onPlay={handleSongPlay}
                  onPause={handleSongPause}
                  onRemix={handleRemix}
                />
              ))}
            </div>
          </div>

          {/* Featured Section */}
          <div className="flex flex-col items-center">
            <div 
              className="flex justify-between items-center mb-3"
              style={{
                width: `${cardSize * 5 + 16 * 4}px` // 5 cards + 4 gaps
              }}
            >
              <h2 className="text-xl font-medium text-white">
                Featured
              </h2>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexGrow: 0,
                  flexShrink: 0,
                  height: 32,
                  position: "relative",
                  paddingLeft: 12,
                  paddingRight: 8,
                  paddingTop: 4,
                  paddingBottom: 4,
                  borderRadius: 16,
                  background: "rgba(255,255,255,0.04)",
                  cursor: "pointer"
                }}
              >
                <p
                  style={{
                    flexGrow: 0,
                    flexShrink: 0,
                    fontSize: 14,
                    fontWeight: 500,
                    textAlign: "left",
                    color: "rgba(255,255,255,0.6)",
                  }}
                >
                  See All
                </p>
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexGrow: 0, flexShrink: 0, width: 16, height: 16, position: "relative" }}
                  preserveAspectRatio="xMidYMid meet"
                >
                  <path
                    d="M9.73515 8.00323L5.60621 3.87429C5.46577 3.73385 5.39789 3.56766 5.40257 3.37573C5.40725 3.18379 5.47981 3.0176 5.62025 2.87716C5.76069 2.73672 5.92688 2.6665 6.11882 2.6665C6.31075 2.6665 6.47694 2.73672 6.61738 2.87716L10.9289 7.20273C11.0412 7.31508 11.1255 7.44147 11.1817 7.58191C11.2379 7.72235 11.266 7.86279 11.266 8.00323C11.266 8.14368 11.2379 8.28412 11.1817 8.42456C11.1255 8.565 11.0412 8.69139 10.9289 8.80374L6.60333 13.1293C6.46289 13.2697 6.29905 13.3376 6.11179 13.3329C5.92454 13.3283 5.76069 13.2557 5.62025 13.1153C5.47981 12.9748 5.40959 12.8086 5.40959 12.6167C5.40959 12.4248 5.47981 12.2586 5.62025 12.1181L9.73515 8.00323Z"
                    fill="white"
                    fillOpacity="0.6"
                  />
                </svg>
              </div>
            </div>
            <div 
              className="grid gap-4"
              style={{
                gridTemplateColumns: `repeat(5, ${cardSize}px)`,
                justifyContent: 'center'
              }}
            >
              {featuredSongs.map((song) => (
                <SongCard
                  key={song.id}
                  song={song}
                  size={cardSize}
                  isPlaying={currentlyPlayingSong?.id === song.id}
                  onPlay={handleSongPlay}
                  onPause={handleSongPause}
                  onRemix={handleRemix}
                />
              ))}
            </div>
          </div>
        </div>
        )}

        {/* Sticky Input Bar */}
        {isSticky && (
          <div 
            className="fixed top-0 z-50"
            style={{ 
              left: isNavCollapsed ? '80px' : '240px', // Adjust based on navbar state
              right: '0',
              background: `linear-gradient(to bottom, ${colors.bg.primary} 0%, rgba(16, 16, 18, 0) 100%)`
            }}
          >
            <div className="flex items-center justify-center w-full px-6 py-3">
              <div 
                className="flex items-center w-full max-w-[696px] px-2 py-2 rounded-full"
                style={{ 
                  background: 'rgba(255,255,255,0.04)', 
                  border: '0.5px solid rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}
              >
                {/* Left: Plus Icon */}
                <div 
                  ref={stickyAudioButtonRef}
                  className="flex items-center justify-center w-8 h-8 rounded-full cursor-pointer transition-colors mr-3"
                  style={{ background: 'rgba(255,255,255,0.04)' }}
                  onClick={handleAudioButtonClick}
                >
                  <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 2C7.52661 2 7.14286 2.38376 7.14286 2.85714V7.14286L2.85714 7.14286C2.38376 7.14286 2 7.52661 2 8C2 8.47339 2.38376 8.85714 2.85714 8.85714H7.14286V13.1429C7.14286 13.6162 7.52661 14 8 14C8.47339 14 8.85714 13.6162 8.85714 13.1429V8.85714H13.1429C13.6162 8.85714 14 8.47339 14 8C14 7.52661 13.6162 7.14286 13.1429 7.14286L8.85714 7.14286V2.85714C8.85714 2.38376 8.47339 2 8 2Z" fill="#F7F4EF"/>
                  </svg>
                </div>

                {/* Center: Input Field */}
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Chat to make music"
                  className="flex-1 bg-transparent border-none outline-none text-sm"
                  style={{ 
                    fontFamily: 'PP Neue Montreal',
                    fontWeight: 500,
                    color: colors.text.secondary,
                    caretColor: colors.accent
                  }}
                />

                {/* Right: Gear Icon and Microphone/Checkmark */}
                <div className="flex items-center gap-2 ml-3">
                  <div 
                    className="flex items-center justify-center w-8 h-8 rounded-full cursor-pointer transition-colors"
                    style={{ background: 'rgba(255,255,255,0.04)' }}
                    onClick={() => onNavigate?.('create', { mode: 'Custom' })}
                  >
                    <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 13.3332C3.81111 13.3332 3.65278 13.2693 3.525 13.1415C3.39722 13.0137 3.33333 12.8554 3.33333 12.6665V8.6665H2.66667C2.47778 8.6665 2.31944 8.60262 2.19167 8.47484C2.06389 8.34706 2 8.18873 2 7.99984C2 7.81095 2.06389 7.65261 2.19167 7.52484C2.31944 7.39706 2.47778 7.33317 2.66667 7.33317H5.33333C5.52222 7.33317 5.68056 7.39706 5.80833 7.52484C5.93611 7.65261 6 7.81095 6 7.99984C6 8.18873 5.93611 8.34706 5.80833 8.47484C5.68056 8.60262 5.52222 8.6665 5.33333 8.6665H4.66667V12.6665C4.66667 12.8554 4.60278 13.0137 4.475 13.1415C4.34722 13.2693 4.18889 13.3332 4 13.3332ZM4 5.99984C3.81111 5.99984 3.65278 5.93595 3.525 5.80817C3.39722 5.68039 3.33333 5.52206 3.33333 5.33317V3.33317C3.33333 3.14428 3.39722 2.98595 3.525 2.85817C3.65278 2.73039 3.81111 2.6665 4 2.6665C4.18889 2.6665 4.34722 2.73039 4.475 2.85817C4.60278 2.98595 4.66667 3.14428 4.66667 3.33317V5.33317C4.66667 5.52206 4.60278 5.68039 4.475 5.80817C4.34722 5.93595 4.18889 5.99984 4 5.99984ZM6.66667 5.99984C6.47778 5.99984 6.31944 5.93595 6.19167 5.80817C6.06389 5.68039 6 5.52206 6 5.33317C6 5.14428 6.06389 4.98595 6.19167 4.85817C6.31944 4.73039 6.47778 4.6665 6.66667 4.6665H7.33333V3.33317C7.33333 3.14428 7.39722 2.98595 7.525 2.85817C7.65278 2.73039 7.81111 2.6665 8 2.6665C8.18889 2.6665 8.34722 2.73039 8.475 2.85817C8.60278 2.98595 8.66667 3.14428 8.66667 3.33317V4.6665H9.33333C9.52222 4.6665 9.68056 4.73039 9.80833 4.85817C9.93611 4.98595 10 5.14428 10 5.33317C10 5.52206 9.93611 5.68039 9.80833 5.80817C9.68056 5.93595 9.52222 5.99984 9.33333 5.99984H6.66667ZM8 13.3332C7.81111 13.3332 7.65278 13.2693 7.525 13.1415C7.39722 13.0137 7.33333 12.8554 7.33333 12.6665V7.99984C7.33333 7.81095 7.39722 7.65261 7.525 7.52484C7.65278 7.39706 7.81111 7.33317 8 7.33317C8.18889 7.33317 8.34722 7.39706 8.475 7.52484C8.60278 7.65261 8.66667 7.81095 8.66667 7.99984V12.6665C8.66667 12.8554 8.60278 13.0137 8.475 13.1415C8.34722 13.2693 8.18889 13.3332 8 13.3332ZM12 13.3332C11.8111 13.3332 11.6528 13.2693 11.525 13.1415C11.3972 13.0137 11.3333 12.8554 11.3333 12.6665V11.3332H10.6667C10.4778 11.3332 10.3194 11.2693 10.1917 11.1415C10.0639 11.0137 10 10.8554 10 10.6665C10 10.4776 10.0639 10.3193 10.1917 10.1915C10.3194 10.0637 10.4778 9.99984 10.6667 9.99984H13.3333C13.5222 9.99984 13.6806 10.0637 13.8083 10.1915C13.9361 10.3193 14 10.4776 14 10.6665C14 10.8554 13.9361 11.0137 13.8083 11.1415C13.6806 11.2693 13.5222 11.3332 13.3333 11.3332H12.6667V12.6665C12.6667 12.8554 12.6028 13.0137 12.475 13.1415C12.3472 13.2693 12.1889 13.3332 12 13.3332ZM12 8.6665C11.8111 8.6665 11.6528 8.60262 11.525 8.47484C11.3972 8.34706 11.3333 8.18873 11.3333 7.99984V3.33317C11.3333 3.14428 11.3972 2.98595 11.525 2.85817C11.6528 2.73039 11.8111 2.6665 12 2.6665C12.1889 2.6665 12.3472 2.73039 12.475 2.85817C12.6028 2.98595 12.6667 3.14428 12.6667 3.33317V7.99984C12.6667 8.18873 12.6028 8.34706 12.475 8.47484C12.3472 8.60262 12.1889 8.6665 12 8.6665Z" fill="#F7F4EF"/>
                    </svg>
                  </div>

                  <button
                    onClick={handleGenerate}
                    disabled={!inputValue.trim() || isGenerating}
                    className={`flex items-center justify-center transition-all duration-200 ${
                      inputValue.trim() ? 'w-7 h-7 rounded-full' : 'w-5 h-5'
                    }`}
                    style={{
                      backgroundImage: inputValue.trim() ? "url('/aura.png')" : 'none',
                      backgroundSize: inputValue.trim() ? "cover" : 'auto',
                      backgroundPosition: inputValue.trim() ? "center" : 'initial',
                      backgroundRepeat: inputValue.trim() ? "no-repeat" : 'repeat',
                      opacity: inputValue.trim() ? 1 : 0.3
                    }}
                  >
                    {isGenerating ? (
                      <div className="w-3 h-3">
                        <SmallSpinner />
                      </div>
                    ) : inputValue.trim() ? (
                      <svg width={12} height={12} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.3334 4.66663L6.00008 12L2.66675 8.66663" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width={20} height={20} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.9472 14C11.1172 14 10.4072 13.71 9.8272 13.12C9.2472 12.54 8.9472 11.83 8.9472 11V5C8.9472 4.17 9.2372 3.46 9.8272 2.88C10.4072 2.3 11.1172 2 11.9472 2C12.7772 2 13.4872 2.29 14.0672 2.88C14.6472 3.47 14.9472 4.17 14.9472 5V11C14.9472 11.83 14.6572 12.54 14.0672 13.12C13.4772 13.7 12.7772 14 11.9472 14ZM11.9472 21C11.3972 21 10.9472 20.55 10.9472 20V18.8C10.9472 18.3 10.5772 17.88 10.0872 17.76C8.7372 17.41 7.5872 16.69 6.6472 15.6C5.7372 14.54 5.1872 13.34 5.0072 12C4.9372 11.45 5.3872 11 5.9472 11C6.4972 11 6.9372 11.45 7.0372 11.99C7.2172 12.95 7.6772 13.8 8.4072 14.53C9.3872 15.5 10.5572 15.99 11.9472 15.99C13.3372 15.99 14.5072 15.5 15.4872 14.53C16.2172 13.8 16.6772 12.95 16.8572 11.99C16.9572 11.45 17.3972 11 17.9472 11C18.4972 11 18.9572 11.45 18.8872 12C18.7072 13.34 18.1672 14.54 17.2472 15.6C16.3072 16.69 15.1572 17.41 13.8072 17.76C13.3172 17.88 12.9472 18.3 12.9472 18.8V20C12.9472 20.55 12.4972 21 11.9472 21Z" fill="white" fillOpacity="0.3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Audio Dropdown */}
        {showAudioDropdown && ReactDOM.createPortal(
          <div className="fixed inset-0 z-[9999] pointer-events-none">
            <div 
              className="absolute pointer-events-auto"
              style={{
                left: (audioButtonRef.current?.getBoundingClientRect().left || stickyAudioButtonRef.current?.getBoundingClientRect().left || 0),
                top: ((audioButtonRef.current?.getBoundingClientRect().bottom || stickyAudioButtonRef.current?.getBoundingClientRect().bottom || 0) + 8)
              }}
            >
              <DropdownMenu
                isOpen={showAudioDropdown}
                onClose={() => setShowAudioDropdown(false)}
                options={audioOptions}
                onSelect={handleAudioOptionSelect}
                position="bottom-left"
                width={210}
                showCheck={false}
                anchorRef={audioButtonRef.current ? audioButtonRef : stickyAudioButtonRef}
              />
            </div>
          </div>,
          document.body
        )}
      </div>
    </>
  );
};

export default Home;